import { useCallback, useMemo } from "react";

import { useBlockEnvironment } from "@/domain/public-dashboard/components/dashboard-renderer/block-environment-context";
import { useGetPublicFocusAreasForDashboard } from "@/domain/public-dashboard/hooks/api/use-get-public-focus-areas-for-dashboard";
import { useUnsafeCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { compareFractionalOrder } from "@/lib/compare-fractional-order";

import { useGetFocusAreaList } from "./use-get-focus-area-list";

export interface UseSmartFocusAreaListOptions {
  workspaceId?: string;
  enabled?: boolean;
}

export interface SmartFocusArea {
  id: string;
  name: string;
  color: string;
  icon?: string | null;
  iconType?: string | null;
  workspaceId: string;
  order: string;
}

export interface UseSmartFocusAreaListResult {
  focusAreaList: SmartFocusArea[];
  findFocusAreaById: (id: string) => SmartFocusArea | undefined;
  loading: boolean;
  error: unknown;
  isPublic: boolean;
}

export function useSmartFocusAreaList(options?: UseSmartFocusAreaListOptions): UseSmartFocusAreaListResult {
  const context = useBlockEnvironment();
  const { environment, dashboardId, workspaceId: contextWorkspaceId } = context;

  const currentWorkspace = useUnsafeCurrentWorkspaceContext();
  const workspaceContextId = currentWorkspace?.currentWorkspace?.id;

  const isPublic = environment === "embed" && !!dashboardId;

  const effectiveWorkspaceId = contextWorkspaceId ?? workspaceContextId ?? options?.workspaceId;

  const publicFocusAreas = useGetPublicFocusAreasForDashboard({
    dashboardId: dashboardId ?? "",
    enabled: isPublic && !!dashboardId && options?.enabled !== false,
  });

  const privateFocusAreas = useGetFocusAreaList({
    workspaceId: effectiveWorkspaceId,
    enabled: !isPublic && !!effectiveWorkspaceId && options?.enabled !== false,
  });

  const publicFocusAreaList = useMemo((): SmartFocusArea[] => {
    if (!isPublic || !publicFocusAreas.focusAreaList) return [];

    return publicFocusAreas.focusAreaList
      .map((fa) => ({
        id: fa.id,
        name: fa.name,
        color: fa.color,
        icon: fa.icon ?? null,
        iconType: fa.iconType ?? null,
        workspaceId: fa.workspaceId,
        order: fa.order ?? "",
      }))
      .sort((a, b) => compareFractionalOrder(a.order, b.order));
  }, [isPublic, publicFocusAreas.focusAreaList]);

  const privateFocusAreaList = useMemo((): SmartFocusArea[] => {
    if (isPublic) return [];
    if (!privateFocusAreas.focusAreaList) return [];

    return privateFocusAreas.focusAreaList
      .map((fa) => ({
        id: fa.id,
        name: fa.name,
        color: fa.color,
        icon: fa.icon ?? null,
        iconType: fa.iconType ?? null,
        workspaceId: fa.workspaceId,
        order: fa.order ?? "",
      }))
      .sort((a, b) => compareFractionalOrder(a.order, b.order));
  }, [isPublic, privateFocusAreas.focusAreaList]);

  const focusAreaList = useMemo((): SmartFocusArea[] => {
    return isPublic ? publicFocusAreaList : privateFocusAreaList;
  }, [isPublic, publicFocusAreaList, privateFocusAreaList]);

  const findFocusAreaById = useCallback(
    (id: string) => {
      return focusAreaList.find((fa) => fa.id === id);
    },
    [focusAreaList],
  );

  const loading = isPublic ? publicFocusAreas.loading : privateFocusAreas.loading;
  const error = isPublic ? publicFocusAreas.error : privateFocusAreas.error;

  return {
    focusAreaList,
    findFocusAreaById,
    loading,
    error,
    isPublic,
  };
}
